import React, { useState } from 'react';
import { formatCurrency } from '../utils';
import { useLanguage } from '../../../i18n';

const ComparisonTable = ({ calculationResult, convertToDisplayCurrency }) => {
  const { t, lang } = useLanguage();
  const [showTableInvestTooltip, setShowTableInvestTooltip] = useState(false);
  const [showTableRenewalTooltip, setShowTableRenewalTooltip] = useState(false);

  const currency = calculationResult.currency;
  const locale = calculationResult.display_locale;
  const fc = (val) => formatCurrency(convertToDisplayCurrency(val || 0), currency, locale);

  const investTooltipText = lang === 'de'
    ? 'Investitionskosten entstehen zu Projektbeginn an und beinhalten die folgenden Positionen:\n\n- Luftwärme: Wärmepumpe inkl. Anschluss.\n- Erdwärme: Wärmepumpe inkl. Anschluss, Bohrung & Erdsondensystem'
    : 'Investment costs are incurred at the start of the project and include:\n\n- Air-source: Heat pump incl. connection.\n- Geothermal: Heat pump incl. connection, drilling & probe system';

  const renewalTooltipText = lang === 'de'
    ? 'Erneuerungskosten fallen an, wenn die Wärmepumpe ersetzt werden muss.\n\n- Bei Erdwärme nach 23 Jahren\n- Bei Luftwärme nach 16 Jahren'
    : 'Renewal costs are incurred when the heat pump needs to be replaced.\n\n- Geothermal after 23 years\n- Air-source after 16 years';

  const TooltipBubble = ({ show, text }) => {
    if (!show) return null;
    return (
      <div style={{
        position: 'absolute', bottom: 'calc(100% + 8px)', left: '0',
        padding: '12px 14px', background: '#333', color: 'white', borderRadius: '8px',
        fontSize: '0.8rem', fontWeight: 'normal', whiteSpace: 'pre-line', zIndex: 9999,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)', width: '340px', textAlign: 'left', lineHeight: '1.5'
      }}>
        {text}
        <div style={{ position: 'absolute', bottom: '-6px', left: '100px', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #333' }}></div>
      </div>
    );
  };

  const InfoIcon = ({ show, setShow }) => (
    <div 
      style={{ 
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '14px', height: '14px', borderRadius: '50%', background: '#666',
        color: 'white', fontSize: '0.6rem', fontWeight: 'bold', cursor: 'pointer',
        flexShrink: 0, position: 'relative'
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={() => setShow(!show)}
    >
      i
    </div>
  );

  const rows = [
    { label: t('tableFullCost'), sep: fc(calculationResult.vollkosten_separatus), konv: fc(calculationResult.vollkosten_konventionelle), luft: fc(calculationResult.vollkosten_luftwaerme) },
    {
      label: t('tableInvestCost'),
      hasTooltip: 'invest',
      sep: fc(calculationResult.invest_beginn_separatus), konv: fc(calculationResult.invest_beginn_konventionelle), luft: fc(calculationResult.invest_beginn_luftwaerme)
    },
    {
      label: t('tableRenewalCost'),
      hasTooltip: 'renewal',
      sep: fc(calculationResult.erneuerungskosten_separatus), konv: fc(calculationResult.erneuerungskosten_konventionelle), luft: fc(calculationResult.erneuerungskosten_luftwaerme)
    },
    { label: t('tableOperatingYear'), sep: fc(calculationResult.jahreskosten_separatus), konv: fc(calculationResult.jahreskosten_konventionelle), luft: fc(calculationResult.jahreskosten_luftwaerme) },
    { label: t('tableOperating25'), sep: fc(calculationResult.betrieb25j_separatus), konv: fc(calculationResult.betrieb25j_konventionelle), luft: fc(calculationResult.betrieb25j_luftwaerme), isLast: true },
  ];

  return (
    <div className="results-table-card" style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '1.5rem', overflow: 'visible' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#333' }}>{t('tableOverview')}</h3>
      <div className="results-table-scroll" style={{ overflowX: 'auto' }}>
        <table className="results-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', minWidth: '500px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', color: '#666' }}></th>
              <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: '#4CAF50', fontWeight: '600' }}>{t('tableSeparatus')}</th>
              <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: '#FF9800', fontWeight: '600' }}>{t('tableKonv')}</th>
              <th style={{ textAlign: 'right', padding: '0.75rem 0.5rem', color: '#2196F3', fontWeight: '600' }}>{t('tableLuft')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: row.isLast ? 'none' : '1px solid #f0f0f0' }}>
                <td style={{ padding: '0.75rem 0.5rem', color: '#333', position: row.hasTooltip ? 'relative' : undefined }}>
                  {row.hasTooltip ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      {row.label}
                      <InfoIcon
                        show={row.hasTooltip === 'invest' ? showTableInvestTooltip : showTableRenewalTooltip}
                        setShow={row.hasTooltip === 'invest' ? setShowTableInvestTooltip : setShowTableRenewalTooltip}
                      />
                      <TooltipBubble
                        show={row.hasTooltip === 'invest' ? showTableInvestTooltip : showTableRenewalTooltip}
                        text={row.hasTooltip === 'invest' ? investTooltipText : renewalTooltipText}
                      />
                    </span>
                  ) : row.label}
                </td>
                <td style={{ textAlign: 'right', padding: '0.75rem 0.5rem' }}>{row.sep}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 0.5rem' }}>{row.konv}</td>
                <td style={{ textAlign: 'right', padding: '0.75rem 0.5rem' }}>{row.luft}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparisonTable;
